<script lang="ts">
	// The sidebar "Chats" section: the user's own chats, pinned ones first and
	// then by recency (no date headings), minus the ones that live in a folder
	// (FolderList), the trashed ones (TrashList) and temporary chats (never
	// listed). Rows are SessionRow; the header's check-list toggle switches the
	// sidebar into selection mode for the bulk actions. The section is also a
	// drop zone: a chat dragged out of a folder onto it leaves the folder (SAY-1820).
	import ListChecksIcon from '@lucide/svelte/icons/list-checks';
	import SidebarSection from './SidebarSection.svelte';
	import SessionRow from './SessionRow.svelte';
	import { getSessions, subscribeRelativeTicker } from '$lib/sessions-store.svelte';
	import { markScheduledSessionSeen } from '$lib/scheduled-store.svelte';
	import { getFolderMembership, moveToFolder, pinRank } from '$lib/folders-store.svelte';
	import { isTemporary } from '$lib/temp-chats.svelte';
	import { isSelected, isSelectionMode, setSelectionMode, toggleSelected } from '$lib/chat-selection.svelte';
	import { compareRows, getDraggedChats, isChatDrag } from '$lib/chat-dnd';
	import { toast } from 'svelte-sonner';

	type Props = {
		activeId?: string;
	};

	let { activeId }: Props = $props();

	const sessions = getSessions();
	const membership = getFolderMembership();

	$effect(() => {
		const off = subscribeRelativeTicker();
		return off;
	});

	// Opening a session clears its "new scheduled run" dot. Recorded for the
	// active session regardless of whether it's a known scheduled run yet, so it
	// still wins if the user opens a fresh run before the next schedule poll.
	$effect(() => {
		if (activeId) markScheduledSessionSeen(activeId);
	});

	// The "Chats" section can be folded away (Open WebUI Section), state kept
	// for the tab's lifetime.
	let chatsOpen = $state(true);

	const rows = $derived(
		sessions
			.filter((s) => !s.deletedAt && !membership[s.sessionId] && !isTemporary(s.sessionId))
			.sort(compareRows(pinRank)),
	);

	// Drop zone: chats dragged here leave their folder.
	let dragOver = $state(false);
	function onDragOver(e: DragEvent): void {
		if (!isChatDrag(e.dataTransfer)) return;
		e.preventDefault();
		if (e.dataTransfer) e.dataTransfer.dropEffect = 'move';
		dragOver = true;
	}
	async function onDrop(e: DragEvent): Promise<void> {
		dragOver = false;
		const ids = getDraggedChats(e.dataTransfer).filter((id) => membership[id]);
		if (ids.length === 0) return;
		e.preventDefault();
		try {
			await moveToFolder(ids, null);
		} catch (err) {
			toast.error('Could not move the chat', { description: err instanceof Error ? err.message : String(err) });
		}
	}
</script>

<div
	class="rounded-xl transition {dragOver ? 'bg-sky-500/10 ring-1 ring-sky-400/60 ring-inset' : ''}"
	role="group"
	aria-label="Chats"
	data-testid="chats-dropzone"
	ondragover={onDragOver}
	ondragleave={() => (dragOver = false)}
	ondrop={onDrop}
>
	<SidebarSection name="Chats" id="chats" bind:open={chatsOpen}>
		{#snippet action()}
			<button
				type="button"
				class="flex size-7 items-center justify-center rounded-lg transition-colors duration-100 {isSelectionMode()
					? 'text-gray-700 dark:text-gray-200'
					: 'text-gray-300 hover:text-gray-500 dark:text-gray-600 dark:hover:text-gray-400'}"
				aria-label={isSelectionMode() ? 'Done selecting' : 'Select chats'}
				aria-pressed={isSelectionMode()}
				title={isSelectionMode() ? 'Done selecting' : 'Select chats'}
				data-testid="select-chats-toggle"
				onclick={() => setSelectionMode(!isSelectionMode())}
			>
				<ListChecksIcon class="size-3.5" aria-hidden="true" />
			</button>
		{/snippet}
		{#if rows.length > 0}
			<div class="pt-1.5" role="list">
				{#each rows as s (s.sessionId)}
					<SessionRow
						row={s}
						isActive={s.sessionId === activeId}
						selectable={isSelectionMode()}
						selected={isSelected(s.sessionId)}
						onToggleSelect={(on) => toggleSelected(s.sessionId, on)}
					/>
				{/each}
			</div>
		{:else}
			<p class="px-2 py-0.5 text-[0.6875rem] text-gray-400 dark:text-gray-600">No chats</p>
		{/if}
	</SidebarSection>
</div>
